import { toast } from "sonner";

import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@buildpy/ui/alert-dialog";
import { Button } from "@buildpy/ui/button";
import { Switch } from "@buildpy/ui/switch";
import { formatDate } from "@buildpy/core/lib/format";
import { useRecordAction } from "@/modules/project/ui/use-record-action";
import { deleteReview, setReviewHidden } from "@/api/actions/review";
import type { CustomerReview } from "@buildpy/core/modules/review/types";

interface Props {
  projectId: string;
  review: CustomerReview | null;
  // 대표만 지운다 — 액션(requireOwner)과 정책(customer_review_delete)이 한 번 더 막는다.
  canDelete: boolean;
}

const TEST_ID = "project-review";

// 직원 화면의 고객 후기 — 읽기 · 숨김(누구나) · 지우기(대표만). 쓰기는 고객 링크에서만 한다.
const ReviewCard = ({ projectId, review, canDelete }: Props) => {
  const { pending, run } = useRecordAction();

  return (
    <SectionCard
      data-testid={TEST_ID}
      description="고객이 링크에서 남긴 글입니다. 직원은 쓰거나 고칠 수 없고, 숨기거나 대표가 지울 수 있습니다."
      title="고객 후기">
      {!review ? (
        <p className="text-sm text-muted-foreground" data-testid={`${TEST_ID}-empty`}>
          아직 남긴 후기가 없습니다. 고객 링크의 「내 현장」 화면에서 받을 수 있습니다.
        </p>
      ) : (
        <div className="flex flex-col gap-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium" data-testid={`${TEST_ID}-author`}>
              {review.author_label}
            </span>
            <Pill tone={review.consent_public ? "done" : "neutral"}>{review.consent_public ? "공개 동의" : "비공개"}</Pill>
            {review.hidden && <Pill tone="hold">숨김</Pill>}
            {/* 고객이 쓴 날 — updated_at 은 숨김 토글로도 바뀐다. */}
            <span className="ml-auto text-xs tabular-nums text-muted-foreground" data-testid={`${TEST_ID}-date`}>
              {formatDate(review.created_at)}
            </span>
          </div>
          <p className="whitespace-pre-wrap break-words" data-testid={`${TEST_ID}-body`}>
            {review.body}
          </p>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex min-h-10 items-center gap-x-2" htmlFor="project-review-hidden">
              <Switch
                aria-label="후기 숨기기"
                checked={review.hidden}
                data-testid={`${TEST_ID}-hidden`}
                disabled={pending}
                id="project-review-hidden"
                onCheckedChange={(hidden) =>
                  void run(
                    "숨김을 바꾸지 못했습니다",
                    () => setReviewHidden({ project_id: projectId, review_id: review.id, hidden }),
                    () => toast.success(hidden ? "후기를 숨겼습니다" : "후기 숨김을 풀었습니다")
                  )
                }
              />
              <span>{review.hidden ? "공개 화면에서 숨깁니다" : "숨기지 않습니다"}</span>
            </label>
            {canDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid={`${TEST_ID}-delete`} disabled={pending} size="sm" type="button" variant="destructive">
                    삭제
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>이 후기를 지울까요?</AlertDialogTitle>
                    <AlertDialogDescription>
                      지운 후기는 되돌릴 수 없습니다. 공개 화면에서만 빼려면 숨김을 쓰세요.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>취소</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`${TEST_ID}-delete-confirm`}
                      onClick={() =>
                        void run(
                          "후기를 지우지 못했습니다",
                          () => deleteReview({ project_id: projectId, review_id: review.id }),
                          () => toast.success("후기를 지웠습니다")
                        )
                      }>
                      삭제
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export default ReviewCard;
